import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export interface UserProfile {
  id: string;
  email: string;
  role: "client" | "caregiver" | "admin";
  full_name: string | null;
  phone: string | null;
  address: string | null;
}

export function useAuth() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string, email: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (data) {
      setUser({
        id: userId,
        email,
        role: data.role as UserProfile["role"],
        full_name: data.full_name,
        phone: data.phone,
        address: data.address,
      });
    } else {
      // Create profile if missing
      await supabase.from("profiles").insert({
        id: userId,
        role: "client",
        full_name: null,
        phone: null,
        address: null,
      });
      setUser({
        id: userId,
        email,
        role: "client",
        full_name: null,
        phone: null,
        address: null,
      });
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && mounted) {
        await fetchProfile(session.user.id, session.user.email || "");
      }
      if (mounted) setLoading(false);
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        if (event === "SIGNED_IN" && session?.user) {
          await fetchProfile(session.user.id, session.user.email || "");
        } else if (event === "SIGNED_OUT") {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (!error && data.user) {
      await supabase.from("profiles").insert({
        id: data.user.id,
        role: "client",
        full_name: fullName,
        phone: null,
        address: null,
      });
    }
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  return { user, loading, signIn, signUp, signOut };
}